import { T, useTranslate } from '@tolgee/react';
import { useFlag } from '@unleash/proxy-client-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { QuestionMarkSvg } from '../../../assets/icons';
import { Button } from '../../../components/_atoms/Button';
import useAccessBadge, { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import useExportExcelResults from '../../../hooks/useExportExcelResults';
import { useMetrics } from '../../../hooks/useMetrics';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { SchoolPlanAccentIcon } from '../../_atoms/SchoolPlanAccentIcon/SchoolPlanAccentIcon';
import BaseModal from '../BaseModal';
import CopyLink from '../CopyLink';

const ShareResultsModal = ({
  gameId,
  showShareResults,
  setShowShareResults,
  tenantId,
  gameTitle,
  currentRoundId,
}: {
  gameId: string;
  showShareResults: boolean;
  setShowShareResults: Dispatch<SetStateAction<boolean>>;
  tenantId: string;
  gameTitle?: string | null;
  currentRoundId?: string | null;
}) => {
  const useNewResults = useFlag('useNewResults');
  const metrics = useMetrics();
  const router = useRouter();
  const { urlTenantId: tenantIdRouter, gameId: gameIdRouter } = useRouterMatch();
  const { t } = useTranslate();
  const basePath = router.pathname
    .replace('[tenantId]', tenantIdRouter as string)
    .replace('[gameId]', gameIdRouter as string);

  const session = useSession();
  const { accessBadge: userAccessBadge } = useAccessBadge();

  // Link-sharing a result is free; exporting the results data is not.
  const isFreemiumUser =
    userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.freemium;
  const { promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const {
    download: downloadExcel,
    isLoading: isDownloadingExcel,
  } = useExportExcelResults(gameId);

  const handleExportToExcel = () => {
    if (isFreemiumUser) {
      promptPaywall({
        label: 'Reports list view: Export results to Excel',
        description: 'Report results export',
      });
      return;
    }
    downloadExcel();
  };

  if (currentRoundId) {
    return (
      <BaseModal
        open={showShareResults}
        onCancel={() => setShowShareResults(false)}
        title={t('Share results')}
      >
        <div
          style={{
            marginBottom: '10px',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <IconWrapper style={{ marginRight: '10px' }}>
              <QuestionMarkSvg />
            </IconWrapper>{' '}
            {t('WhoCanAccess', 'Who can access this result?')}
          </span>
          <span style={{ fontWeight: 'bold' }}>
            {t('Anyone with the link can see the result from this game')}
          </span>
        </div>
        <h3 style={{ marginBottom: 0 }}>{t('Link to the results')}</h3>
        <CopyLink
          onCopy={() => {
            metrics.logEvent('LiveGameViwer.Results.ShareReults.CopyLink');
          }}
          link={`${window.location.origin}${basePath}?roundId=${currentRoundId}`}
        />
      </BaseModal>
    );
  }

  return (
    <BaseModal
      open={showShareResults}
      onCancel={() => setShowShareResults(false)}
      title={t('ResultsFor', 'Results for "{title}"', {
        title: gameTitle,
      })}
    >
      {paywallComponent}
      <div
        style={{
          display: 'grid',
          gap: '5px',
          gridTemplateColumns: '1fr',
        }}
      >
        <div
          style={{
            marginBottom: '10px',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <IconWrapper style={{ marginRight: '10px' }}>
              <QuestionMarkSvg />
            </IconWrapper>{' '}
            <T keyName="WhoCanAccess" defaultValue="Who can access this result?" />
          </span>
          <span style={{ fontWeight: 200 }}>
            <T
              keyName="Anyone with the link can see the result from this game"
              defaultValue="Anyone with the link can see the result from this game"
            />
          </span>
        </div>
        <h3 style={{ marginBottom: 0 }}>{t('Link to the results')}</h3>
        {typeof window !== 'undefined' && (
          <>
            <CopyLink
              onCopy={() => {
                metrics.logEvent('Game.Results.ShareReults.CopyLink');
              }}
              link={`${window.location.origin}/${tenantId}/games/${gameId}/live/results`}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Link
                href={`${window.location.origin}/${tenantId}/games/${gameId}/live/results`}
              >
                <a target={'_blank'} rel="noopener noreferrer">
                  <Button
                    type={'secondary'}
                    styles={{ maxWidth: '200px' }}
                    onClick={() => {
                      metrics.logEvent('Game.Results.ViewResults', { fromModal: true });
                    }}
                  >
                    <T keyName="ViewResults" defaultValue="View results" />
                  </Button>
                </a>
              </Link>
              {useNewResults && (
                <Button
                  styles={{ width: 'fit-content' }}
                  loading={isDownloadingExcel}
                  onClick={handleExportToExcel}
                >
                  <T keyName="ExportToExcel" defaultValue="Export to Excel" />
                  {isFreemiumUser && (
                    <BadgeSlot>
                      <SchoolPlanAccentIcon size={18} />
                    </BadgeSlot>
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
};

const BadgeSlot = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
`;

export default ShareResultsModal;
